import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import styled from 'styled-components';
import {
  FaFilter, FaChevronDown, FaChevronUp, FaTimes, FaPaperPlane,
  FaSearch, FaSpinner, FaSms, FaEye, FaCheckSquare, FaSquare,
  FaMinusSquare, FaClock, FaInfoCircle, FaExclamationTriangle,
  FaExchangeAlt, FaSort, FaSortUp, FaSortDown
} from 'react-icons/fa';
import DashboardLayout from '../../../layouts/DashboardLayout';
import Modal from '../../../components/shared/Modal';
import ModalNotification from '../../../components/shared/ModalNotification';
import Pagination from '../../../components/shared/Pagination';
import TemplateSelector from '../components/TemplateSelector';
import VariableInserter from '../components/VariableInserter';
import BulkSmsProgress from '../components/BulkSmsProgress';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { replaceTemplateVariables, replaceWithSampleData } from '../utils/templateUtils';
import { getLeads, getLeadStatuses, updateLeadPipeline } from '../../leads/leadService';
import { sendSms } from '../../../services/communicationService';
import { createScheduledMessage } from '../../../services/scheduledMessageService';

// ── Styled Components ──

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${p => p.theme.spacing(3)};
  flex-wrap: wrap;
  gap: ${p => p.theme.spacing(2)};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  h1 { color: ${p => p.theme.colors.primary}; margin: 0; font-size: 1.4rem; }
  svg { color: ${p => p.theme.colors.primary}; font-size: 1.3rem; }
`;

const Section = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: 12px;
  padding: ${p => p.theme.spacing(3)};
  margin-bottom: ${p => p.theme.spacing(2.5)};
  border: 1.5px solid ${p => p.theme.colors.inputGray}22;
`;

const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${p => p.theme.colors.textPrimary};
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 ${p => p.theme.spacing(2)} 0;

  .step {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${p => p.theme.colors.primary};
    color: #000;
    font-size: 0.75rem;
    font-weight: 700;
    flex-shrink: 0;
  }
`;

// ── Filter Section ──

const FilterToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${p => p.$open ? p.theme.colors.primary + '22' : 'transparent'};
  color: ${p => p.$open ? p.theme.colors.primary : p.theme.colors.textSecondary};
  border: 1.5px solid ${p => p.$open ? p.theme.colors.primary + '66' : p.theme.colors.inputGray + '44'};
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.84rem;
  font-weight: 500;
  margin-bottom: ${p => p.theme.spacing(2)};
  &:hover { border-color: ${p => p.theme.colors.primary}88; }
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${p => p.theme.spacing(2)};
  margin-bottom: ${p => p.theme.spacing(2)};
  overflow: ${p => p.$open ? 'visible' : 'hidden'};
  max-height: ${p => p.$open ? '500px' : '0'};
  opacity: ${p => p.$open ? 1 : 0};
  transition: max-height 0.3s ease, opacity 0.2s ease;
`;

const FilterField = styled.div`
  label {
    display: block;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${p => p.theme.colors.textSecondary};
    margin-bottom: 6px;
  }

  input, select {
    width: 100%;
    padding: 8px 12px;
    background: ${p => p.theme.colors.background};
    border: 1.5px solid ${p => p.theme.colors.inputGray}44;
    border-radius: ${p => p.theme.borderRadius};
    color: ${p => p.theme.colors.textPrimary};
    font-size: 0.85rem;
    &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  }

  select option {
    background: ${p => p.theme.colors.background};
    color: ${p => p.theme.colors.textPrimary};
  }
`;

const AcceptSmsToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.84rem;
  color: ${p => p.theme.colors.textSecondary};
  cursor: pointer;
  margin-bottom: ${p => p.theme.spacing(2)};

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: ${p => p.theme.colors.primary};
  }
`;

const ResultBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${p => p.theme.spacing(1.5)};
  flex-wrap: wrap;
  gap: 8px;

  .count {
    font-size: 0.84rem;
    color: ${p => p.theme.colors.textSecondary};
    strong { color: ${p => p.theme.colors.textPrimary}; font-weight: 600; }
  }
`;

const ClearFiltersBtn = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.error};
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  &:hover { background: ${p => p.theme.colors.error}18; }
`;

// ── Lead Table ──

const TableWrapper = styled.div`
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.inputGray}22;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;

  th, td {
    padding: 10px 14px;
    text-align: left;
    border-bottom: 1px solid ${p => p.theme.colors.inputGray}22;
  }

  th {
    background: ${p => p.theme.colors.background};
    color: ${p => p.theme.colors.textSecondary};
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tr:hover td {
    background: ${p => p.theme.colors.primary}08;
  }
`;

const CheckboxCell = styled.td`
  width: 40px;
  text-align: center !important;
`;

const CheckboxHeader = styled.th`
  width: 40px;
  text-align: center !important;
`;

// The colour needs !important: Table's own `th` rule is more specific than this
// component's single class, so a plain declaration would lose to it.
const SortableHeader = styled.th`
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  color: ${p => (p.$active ? p.theme.colors.primary : 'inherit')} !important;

  &:hover { color: ${p => p.theme.colors.primary} !important; }

  span { margin-right: 6px; }
  svg { font-size: 0.7rem; vertical-align: -1px; }

  /* The neutral arrows only show on hover, so the header row stays quiet until
     you go looking for a sort. */
  svg.idle { opacity: 0; transition: opacity 0.15s; }
  &:hover svg.idle, &:focus-visible svg.idle { opacity: 0.45; }
`;

const CheckBtn = styled.button`
  background: none;
  border: none;
  color: ${p => p.checked ? p.theme.colors.primary : p.theme.colors.inputGray};
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${p => p.disabled ? 0.3 : 1};
  font-size: 16px;
  padding: 2px;
  display: flex;
  &:hover:not(:disabled) { color: ${p => p.theme.colors.primary}; }
`;

const PipelineBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${p => p.color}22;
  color: ${p => p.color};
  white-space: nowrap;
`;

const DisabledRow = styled.tr`
  opacity: 0.4;
  td { color: ${p => p.theme.colors.inputGray} !important; }
`;

const SelectedCount = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${p => p.theme.colors.primary}15;
  border: 1px solid ${p => p.theme.colors.primary}33;
  border-radius: 8px;
  margin-top: ${p => p.theme.spacing(1.5)};
  font-size: 0.88rem;
  color: ${p => p.theme.colors.primary};
  font-weight: 500;
`;

// ── Message Section ──

const MessageTextarea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  background: ${p => p.theme.colors.background};
  border: 1.5px solid ${p => p.theme.colors.inputGray}44;
  border-radius: ${p => p.theme.borderRadius};
  color: ${p => p.theme.colors.textPrimary};
  font-size: 0.9rem;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  line-height: 1.5;
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.inputGray}; }
`;

const CharCount = styled.div`
  text-align: right;
  font-size: 0.78rem;
  color: ${p => p.count > 240 ? p.theme.colors.error : p.theme.colors.textSecondary};
  margin-top: 4px;
`;

const PreviewBox = styled.div`
  background: ${p => p.theme.colors.background};
  border: 1px dashed ${p => p.theme.colors.inputGray}55;
  border-radius: ${p => p.theme.borderRadius};
  padding: 14px;
  margin-top: ${p => p.theme.spacing(2)};

  .preview-label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${p => p.theme.colors.textSecondary};
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .preview-text {
    color: ${p => p.theme.colors.textPrimary};
    font-size: 0.88rem;
    line-height: 1.5;
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  .preview-empty {
    color: ${p => p.theme.colors.inputGray};
    font-style: italic;
  }
`;

// ── Rate Limit Section ──

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing(2)};
  flex-wrap: wrap;
`;

const Slider = styled.input`
  flex: 1;
  min-width: 180px;
  accent-color: ${p => p.theme.colors.primary};
  height: 6px;
`;

const SliderValue = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${p => p.theme.colors.primary};
  white-space: nowrap;
`;

const EstimateTime = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: ${p => p.theme.spacing(1.5)};
  font-size: 0.84rem;
  color: ${p => p.theme.colors.textSecondary};

  svg { color: ${p => p.theme.colors.primary}; }
  strong { color: ${p => p.theme.colors.textPrimary}; }
`;

const InfoText = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: ${p => p.theme.spacing(1.5)};
  padding: 10px 14px;
  background: ${p => p.theme.colors.primary}10;
  border-radius: 8px;
  font-size: 0.8rem;
  color: ${p => p.theme.colors.textSecondary};
  line-height: 1.4;

  svg { color: ${p => p.theme.colors.primary}; flex-shrink: 0; margin-top: 2px; }
`;

// ── Send Button ──

const SendBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 24px;
  background: ${p => p.theme.colors.primary};
  color: #000;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;

  &:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const WarningBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${p => p.theme.colors.error}15;
  border: 1px solid ${p => p.theme.colors.error}33;
  border-radius: 8px;
  margin-bottom: ${p => p.theme.spacing(2)};
  font-size: 0.84rem;
  color: ${p => p.theme.colors.error};
`;

// ── Pipeline Stage Helpers ──

const PIPELINE_STAGES = [
  { value: 'new', label: 'New', color: '#4FC3F7' },
  { value: 'first_contact', label: 'First Contact', color: '#81C784' },
  { value: 'quote_sent', label: 'Quote Sent', color: '#FFB74D' },
  { value: 'quote_opened', label: 'Quote Opened', color: '#FF8A65' },
  { value: 'follow_up_1', label: '1st Follow Up', color: '#BA68C8' },
  { value: 'follow_up_2', label: '2nd Follow Up', color: '#9575CD' },
  { value: 'follow_up_3', label: '3rd Follow Up', color: '#7986CB' },
  { value: 'won', label: 'Won', color: '#66BB6A' },
  { value: 'lost', label: 'Lost', color: '#ef5350' },
];

// A lead's status lives either directly on the lead or on its first service
// request; every read of it goes through here.
const getStatusName = (lead) =>
  lead.status?.status_name || lead.serviceRequests?.[0]?.LeadStatus?.status_name || '';

const getPipelineInfo = (stage) =>
  PIPELINE_STAGES.find(s => s.value === stage) || { label: stage, color: '#999' };

const isValidPhone = (phone) => {
  if (!phone) return false;
  const digits = phone.replace(/\D/g, '');
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
};

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const formatEstimateDate = (lead) => {
  const sentEstimate = lead.estimates?.find(e => e.status === 'Sent' || e.status === 'Viewed' || e.status === 'Accepted');
  if (!sentEstimate) return null;
  return new Date(sentEstimate.createdAt);
};

const getLatestEstimate = (lead) => {
  if (!lead.estimates || lead.estimates.length === 0) return null;
  const sent = lead.estimates.filter(e => e.publicToken);
  if (sent.length === 0) return lead.estimates[0];
  return sent.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
};

// ── Sorting ──
//
// Key order here is the column order in the table, and the body cells are still
// written out by hand — reorder these and the <td>s have to move with them.
// Dates open on "desc" because newest-first is what you actually want to see.
const SORTABLE_COLUMNS = {
  name:    { label: 'Name',  defaultDir: 'asc',  value: (l) => (l.name || '').toLowerCase() },
  phone:   { label: 'Phone', defaultDir: 'asc',  value: (l) => (l.phone || '').replace(/\D/g, '') },
  city:    { label: 'City',  defaultDir: 'asc',  value: (l) => (l.city || '').toLowerCase() },
  stage:   {
    label: 'Stage',
    defaultDir: 'asc',
    // By pipeline position, not alphabetically: New → First Contact → … → Lost.
    value: (l) => {
      const idx = PIPELINE_STAGES.findIndex(st => st.value === l.pipeline_stage);
      return idx === -1 ? null : idx;
    },
  },
  status:  { label: 'Status', defaultDir: 'asc', value: (l) => getStatusName(l).toLowerCase() },
  updated: {
    label: 'Updated',
    defaultDir: 'desc',
    value: (l) => (l.updatedAt ? new Date(l.updatedAt).getTime() : null),
  },
};

const isBlankSortValue = (v) => v === '' || v === null || v === undefined || Number.isNaN(v);

const compareLeads = (a, b, key, dir) => {
  const { value } = SORTABLE_COLUMNS[key];
  const av = value(a);
  const bv = value(b);

  // Rows with nothing in the sorted column sit at the bottom in both
  // directions — flipping direction shouldn't bury the rows you can act on.
  if (isBlankSortValue(av) || isBlankSortValue(bv)) {
    if (isBlankSortValue(av) && isBlankSortValue(bv)) return 0;
    return isBlankSortValue(av) ? 1 : -1;
  }

  const cmp = (typeof av === 'number' && typeof bv === 'number')
    ? av - bv
    : String(av).localeCompare(String(bv), undefined, { numeric: true });

  return dir === 'desc' ? -cmp : cmp;
};

// ── Filter persistence ──
//
// Reps work this page in passes — filter down, send a batch, come back later —
// and re-entering nine filter fields after every refresh made that tedious, so
// the whole filter set (plus rows per page) is mirrored to localStorage.
// Access is guarded: localStorage can throw outright (private mode, site data
// blocked) and this is a convenience that must never break the page.
const FILTERS_KEY = 'bulkSms.filters';

const DEFAULT_FILTERS = {
  nameSearch: '',
  cityFilter: '',
  pipelineFilter: '',
  statusFilter: '',
  acceptSmsOnly: true,
  quoteDateFrom: '',
  quoteDateTo: '',
  updatedFrom: '',
  updatedTo: '',
  itemsPerPage: 25,
  sortKey: '',
  sortDir: 'asc',
};

const readStoredFilters = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(FILTERS_KEY));
    if (!saved || typeof saved !== 'object') return DEFAULT_FILTERS;
    // Take only known keys whose stored type still matches the default, so a
    // stale or hand-edited blob can't feed garbage into the filter inputs.
    const merged = { ...DEFAULT_FILTERS };
    Object.keys(DEFAULT_FILTERS).forEach(key => {
      if (typeof saved[key] === typeof DEFAULT_FILTERS[key]) merged[key] = saved[key];
    });
    // A type check isn't enough for the sort: the column has to be one we still
    // know how to sort by, and the direction one of the two real values.
    if (!SORTABLE_COLUMNS[merged.sortKey]) merged.sortKey = '';
    if (merged.sortDir !== 'asc' && merged.sortDir !== 'desc') merged.sortDir = 'asc';
    return merged;
  } catch (e) {
    return DEFAULT_FILTERS;
  }
};

const writeStoredFilters = (filters) => {
  try {
    window.localStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
  } catch (e) {
    /* persistence is a nicety — never break the page over it */
  }
};

// ── Component ──

const BulkSmsPage = () => {
  // Data
  const [leads, setLeads] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters — seeded from the last visit's saved set (see readStoredFilters).
  const [savedFilters] = useState(readStoredFilters);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [nameSearch, setNameSearch] = useState(savedFilters.nameSearch);
  const [cityFilter, setCityFilter] = useState(savedFilters.cityFilter);
  const [pipelineFilter, setPipelineFilter] = useState(savedFilters.pipelineFilter);
  const [statusFilter, setStatusFilter] = useState(savedFilters.statusFilter);
  const [acceptSmsOnly, setAcceptSmsOnly] = useState(savedFilters.acceptSmsOnly);
  const [quoteDateFrom, setQuoteDateFrom] = useState(savedFilters.quoteDateFrom);
  const [quoteDateTo, setQuoteDateTo] = useState(savedFilters.quoteDateTo);
  const [updatedFrom, setUpdatedFrom] = useState(savedFilters.updatedFrom);
  const [updatedTo, setUpdatedTo] = useState(savedFilters.updatedTo);

  // Sorting — restored with the filters, so a refresh keeps the same view.
  const [sortKey, setSortKey] = useState(savedFilters.sortKey);
  const [sortDir, setSortDir] = useState(savedFilters.sortDir);

  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Message
  const [message, setMessage] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  // Rate limiting
  const [delaySeconds, setDelaySeconds] = useState(3);

  // Scheduling
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(null);
  const [scheduling, setScheduling] = useState(false);

  // Sending state
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState({ total: 0, sent: 0, succeeded: 0, failed: 0, failedDetails: [] });
  const [isPaused, setIsPaused] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  // Notification
  const [feedback, setFeedback] = useState({ show: false, message: '', success: true });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(savedFilters.itemsPerPage);

  // Bulk stage update
  const [bulkStage, setBulkStage] = useState('');
  const [updatingStage, setUpdatingStage] = useState(false);
  const [stageProgress, setStageProgress] = useState({ done: 0, total: 0 });

  // Refs for async control
  const cancelledRef = useRef(false);
  const pausedRef = useRef(false);
  const messageRef = useRef(null);

  // Fetch leads and statuses
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [leadsData, statusesData] = await Promise.all([
          getLeads(),
          getLeadStatuses(),
        ]);
        setLeads(leadsData || []);
        setStatuses(statusesData?.data || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        setFeedback({ show: true, message: 'Failed to load leads', success: false });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filtered leads
  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      // Name search
      if (nameSearch && !lead.name?.toLowerCase().includes(nameSearch.toLowerCase())) return false;

      // City
      if (cityFilter && !lead.city?.toLowerCase().includes(cityFilter.toLowerCase())) return false;

      // Pipeline stage
      if (pipelineFilter && lead.pipeline_stage !== pipelineFilter) return false;

      // Status
      if (statusFilter) {
        const leadStatus = getStatusName(lead);
        if (leadStatus !== statusFilter) return false;
      }

      // Accept SMS only
      if (acceptSmsOnly && !lead.accept_sms) return false;

      // Quote sent date range
      if (quoteDateFrom || quoteDateTo) {
        const estDate = formatEstimateDate(lead);
        if (!estDate) return false;
        if (quoteDateFrom && estDate < new Date(quoteDateFrom)) return false;
        if (quoteDateTo && estDate > new Date(quoteDateTo + 'T23:59:59')) return false;
      }

      // Updated date range
      if (updatedFrom && new Date(lead.updatedAt) < new Date(updatedFrom)) return false;
      if (updatedTo && new Date(lead.updatedAt) > new Date(updatedTo + 'T23:59:59')) return false;

      return true;
    });
  }, [leads, nameSearch, cityFilter, pipelineFilter, statusFilter, acceptSmsOnly, quoteDateFrom, quoteDateTo, updatedFrom, updatedTo]);

  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / itemsPerPage));

  // Remember the filter set for the next visit
  useEffect(() => {
    writeStoredFilters({
      nameSearch, cityFilter, pipelineFilter, statusFilter, acceptSmsOnly,
      quoteDateFrom, quoteDateTo, updatedFrom, updatedTo, itemsPerPage,
      sortKey, sortDir,
    });
  }, [
    nameSearch, cityFilter, pipelineFilter, statusFilter, acceptSmsOnly,
    quoteDateFrom, quoteDateTo, updatedFrom, updatedTo, itemsPerPage,
    sortKey, sortDir,
  ]);

  // Reset page on filter or sort change
  useEffect(() => { setCurrentPage(1); }, [
    nameSearch, cityFilter, pipelineFilter, statusFilter, acceptSmsOnly,
    quoteDateFrom, quoteDateTo, updatedFrom, updatedTo, sortKey, sortDir,
  ]);

  // Keep the current page inside range when the list shrinks or rows per page grows
  useEffect(() => { setCurrentPage(p => Math.min(p, totalPages)); }, [totalPages]);

  // Sorted leads — ordering the whole filtered set, not just the visible page.
  const sortedLeads = useMemo(() => {
    if (!sortKey || !SORTABLE_COLUMNS[sortKey]) return filteredLeads;
    // slice() first: sort() mutates, and filteredLeads is a memoised value.
    return filteredLeads.slice().sort((a, b) => compareLeads(a, b, sortKey, sortDir));
  }, [filteredLeads, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(SORTABLE_COLUMNS[key].defaultDir);
    }
  };

  // Paginated leads
  const paginatedLeads = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedLeads.slice(start, start + itemsPerPage);
  }, [sortedLeads, currentPage, itemsPerPage]);

  // Selectable leads (valid phone)
  const selectableLeads = useMemo(() => {
    return filteredLeads.filter(l => isValidPhone(l.phone));
  }, [filteredLeads]);

  // Select all / none
  const allSelected = selectableLeads.length > 0 && selectableLeads.every(l => selectedIds.has(l.id));
  const someSelected = selectableLeads.some(l => selectedIds.has(l.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableLeads.map(l => l.id)));
    }
  };

  const toggleLead = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Clear filters
  const clearFilters = () => {
    setNameSearch('');
    setCityFilter('');
    setPipelineFilter('');
    setStatusFilter('');
    setAcceptSmsOnly(true);
    setQuoteDateFrom('');
    setQuoteDateTo('');
    setUpdatedFrom('');
    setUpdatedTo('');
  };

  const hasActiveFilters = nameSearch || cityFilter || pipelineFilter || statusFilter || !acceptSmsOnly || quoteDateFrom || quoteDateTo || updatedFrom || updatedTo;

  // Template selection
  const handleTemplateSelect = (template) => {
    if (template) {
      setSelectedTemplateId(template.id);
      setMessage(template.body);
    } else {
      setSelectedTemplateId(null);
      setMessage('');
    }
  };

  // Variable insertion at cursor
  const handleInsertVariable = (variable) => {
    const textarea = messageRef.current;
    if (!textarea) {
      setMessage(prev => prev + variable);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newMsg = message.substring(0, start) + variable + message.substring(end);
    setMessage(newMsg);
    setTimeout(() => {
      textarea.focus();
      const pos = start + variable.length;
      textarea.setSelectionRange(pos, pos);
    }, 0);
  };

  // Preview
  const previewLead = useMemo(() => {
    const firstSelected = leads.find(l => selectedIds.has(l.id));
    return firstSelected || null;
  }, [leads, selectedIds]);

  const previewText = useMemo(() => {
    if (!message) return '';
    if (previewLead) {
      return replaceTemplateVariables(message, previewLead, getLatestEstimate(previewLead));
    }
    return replaceWithSampleData(message);
  }, [message, previewLead]);

  // Estimated time
  const estimatedTime = useMemo(() => {
    const totalSeconds = selectedIds.size * delaySeconds;
    if (totalSeconds < 60) return `${totalSeconds} seconds`;
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins} minutes`;
  }, [selectedIds.size, delaySeconds]);

  // ── Bulk Send ──

  const startSending = useCallback(async () => {
    setConfirmModalOpen(false);
    setSending(true);
    setIsComplete(false);
    setIsPaused(false);
    cancelledRef.current = false;
    pausedRef.current = false;

    const selectedLeads = leads.filter(l => selectedIds.has(l.id));
    const prog = { total: selectedLeads.length, sent: 0, succeeded: 0, failed: 0, failedDetails: [] };
    setProgress({ ...prog });

    for (const lead of selectedLeads) {
      if (cancelledRef.current) break;

      // Wait while paused
      while (pausedRef.current) {
        await sleep(500);
        if (cancelledRef.current) break;
      }
      if (cancelledRef.current) break;

      const estimate = getLatestEstimate(lead);
      const substituted = replaceTemplateVariables(message, lead, estimate);

      try {
        await sendSms(lead.phone, substituted, lead.id);
        prog.succeeded++;
      } catch (err) {
        prog.failed++;
        prog.failedDetails.push({
          leadId: lead.id,
          leadName: lead.name,
          error: err.response?.data?.message || err.message || 'Unknown error',
        });
      }

      prog.sent++;
      setProgress({ ...prog });

      // Rate limit delay (skip on last message)
      if (prog.sent < prog.total && !cancelledRef.current) {
        await sleep(delaySeconds * 1000);
      }
    }

    setIsComplete(true);
    setSending(false);
  }, [leads, selectedIds, message, delaySeconds]);

  // Schedule the whole selection as one batch
  const handleSchedule = useCallback(async () => {
    if (!scheduledAt || scheduledAt.getTime() <= Date.now()) {
      setFeedback({ show: true, message: 'Pick a date/time in the future', success: false });
      return;
    }
    const selectedLeads = leads.filter(l => selectedIds.has(l.id));
    if (selectedLeads.length === 0 || !message.trim()) return;

    setScheduling(true);
    try {
      const recipients = selectedLeads.map(lead => ({
        lead_id: lead.id,
        to_number: lead.phone,
        body: replaceTemplateVariables(message, lead, getLatestEstimate(lead)),
        recipient_name: lead.name,
      }));

      const res = await createScheduledMessage({
        scheduled_time: scheduledAt.toISOString(),
        template_id: selectedTemplateId || null,
        recipients,
      });

      setFeedback({
        show: true,
        message: `Scheduled ${res.count} message${res.count !== 1 ? 's' : ''} for ${scheduledAt.toLocaleString()}`,
        success: true,
      });
      setSelectedIds(new Set());
      setMessage('');
      setSelectedTemplateId(null);
      setScheduledAt(null);
      setScheduleMode(false);
    } catch (err) {
      setFeedback({
        show: true,
        message: err.response?.data?.message || 'Failed to schedule messages',
        success: false,
      });
    } finally {
      setScheduling(false);
    }
  }, [scheduledAt, leads, selectedIds, message, selectedTemplateId]);

  const handlePause = () => {
    pausedRef.current = true;
    setIsPaused(true);
  };

  const handleResume = () => {
    pausedRef.current = false;
    setIsPaused(false);
  };

  const handleCancel = () => {
    cancelledRef.current = true;
    pausedRef.current = false;
    setIsPaused(false);
  };

  const handleDone = () => {
    setSending(false);
    setIsComplete(false);
    setSelectedIds(new Set());
    setMessage('');
    setSelectedTemplateId(null);
    setProgress({ total: 0, sent: 0, succeeded: 0, failed: 0, failedDetails: [] });
  };

  // ── Bulk Pipeline Stage Update ──

  const handleBulkStageUpdate = async () => {
    if (!bulkStage || selectedIds.size === 0) return;
    setUpdatingStage(true);
    const ids = Array.from(selectedIds);
    setStageProgress({ done: 0, total: ids.length });
    let successCount = 0;
    let failCount = 0;

    for (const id of ids) {
      try {
        await updateLeadPipeline(id, bulkStage);
        successCount++;
      } catch (err) {
        failCount++;
        console.error(`Failed to update lead ${id} pipeline:`, err);
      }
      setStageProgress({ done: successCount + failCount, total: ids.length });
    }

    // Update local state so the table reflects the change
    setLeads(prev => prev.map(l =>
      selectedIds.has(l.id) ? { ...l, pipeline_stage: bulkStage } : l
    ));

    setUpdatingStage(false);
    setBulkStage('');
    setFeedback({
      show: true,
      message: failCount > 0
        ? `Updated ${successCount} leads, ${failCount} failed`
        : `${successCount} leads moved to "${getPipelineInfo(bulkStage).label}"`,
      success: failCount === 0,
    });
  };

  // Available statuses for filter
  const availableStatuses = useMemo(() => {
    const set = new Set();
    leads.forEach(l => {
      const s = getStatusName(l);
      if (s) set.add(s);
    });
    return Array.from(set).sort();
  }, [leads]);

  // A status restored from a previous visit can no longer exist (renamed, or no
  // lead carries it any more). The <select> would silently fall back to showing
  // "All Statuses" while still filtering every lead out, so drop it once the
  // leads are in and we know what is actually available.
  useEffect(() => {
    if (!loading && statusFilter && !availableStatuses.includes(statusFilter)) {
      setStatusFilter('');
    }
  }, [loading, statusFilter, availableStatuses]);

  const availableCities = useMemo(() => {
    const set = new Set();
    leads.forEach(l => { if (l.city) set.add(l.city); });
    return Array.from(set).sort();
  }, [leads]);

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px', color: '#F69620' }}>
          <FaSpinner className="spinning" style={{ fontSize: 32, animation: 'spin 1s linear infinite' }} />
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader>
        <TitleRow>
          <FaSms />
          <h1>Bulk SMS</h1>
        </TitleRow>
      </PageHeader>

      {/* ── Section 1: Filters & Lead Selection ── */}
      <Section>
        <SectionTitle>
          <span className="step">1</span>
          Select Leads
        </SectionTitle>

        <FilterToggle $open={filtersOpen} onClick={() => setFiltersOpen(!filtersOpen)}>
          <FaFilter size={12} />
          Filters
          {hasActiveFilters && <span style={{ marginLeft: 4, fontWeight: 700 }}>&bull;</span>}
          {filtersOpen ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
        </FilterToggle>

        <FilterGrid $open={filtersOpen}>
          <FilterField>
            <label>Name</label>
            <input
              value={nameSearch}
              onChange={e => setNameSearch(e.target.value)}
              placeholder="Search by name..."
            />
          </FilterField>
          <FilterField>
            <label>City</label>
            <input
              value={cityFilter}
              onChange={e => setCityFilter(e.target.value)}
              placeholder="Filter by city..."
            />
          </FilterField>
          <FilterField>
            <label>Pipeline Stage</label>
            <select value={pipelineFilter} onChange={e => setPipelineFilter(e.target.value)}>
              <option value="">All Stages</option>
              {PIPELINE_STAGES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </FilterField>
          <FilterField>
            <label>Lead Status</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              {availableStatuses.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </FilterField>
          <FilterField>
            <label>Quote Sent From</label>
            <input type="date" value={quoteDateFrom} onChange={e => setQuoteDateFrom(e.target.value)} />
          </FilterField>
          <FilterField>
            <label>Quote Sent To</label>
            <input type="date" value={quoteDateTo} onChange={e => setQuoteDateTo(e.target.value)} />
          </FilterField>
          <FilterField>
            <label>Updated From</label>
            <input type="date" value={updatedFrom} onChange={e => setUpdatedFrom(e.target.value)} />
          </FilterField>
          <FilterField>
            <label>Updated To</label>
            <input type="date" value={updatedTo} onChange={e => setUpdatedTo(e.target.value)} />
          </FilterField>
        </FilterGrid>

        <AcceptSmsToggle>
          <input
            type="checkbox"
            checked={acceptSmsOnly}
            onChange={e => setAcceptSmsOnly(e.target.checked)}
          />
          Only show leads that accept SMS
        </AcceptSmsToggle>

        <ResultBar>
          <span className="count">
            Showing <strong>{filteredLeads.length}</strong> of {leads.length} leads
          </span>
          {hasActiveFilters && (
            <ClearFiltersBtn onClick={clearFilters}>
              <FaTimes size={10} /> Clear filters
            </ClearFiltersBtn>
          )}
        </ResultBar>

        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <CheckboxHeader>
                  <CheckBtn
                    checked={allSelected}
                    onClick={toggleSelectAll}
                    disabled={selectableLeads.length === 0}
                  >
                    {allSelected ? <FaCheckSquare /> : someSelected ? <FaMinusSquare /> : <FaSquare />}
                  </CheckBtn>
                </CheckboxHeader>
                {Object.entries(SORTABLE_COLUMNS).map(([key, col]) => {
                  const active = sortKey === key;
                  return (
                    <SortableHeader
                      key={key}
                      $active={active}
                      onClick={() => toggleSort(key)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSort(key); }
                      }}
                      tabIndex={0}
                      title={`Sort by ${col.label}`}
                      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    >
                      <span>{col.label}</span>
                      {active
                        ? (sortDir === 'asc' ? <FaSortUp /> : <FaSortDown />)
                        : <FaSort className="idle" />}
                    </SortableHeader>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {paginatedLeads.map(lead => {
                const validPhone = isValidPhone(lead.phone);
                const canSelect = validPhone;
                const isSelected = selectedIds.has(lead.id);
                const pipeInfo = getPipelineInfo(lead.pipeline_stage);
                const statusName = getStatusName(lead) || '-';
                const Row = canSelect ? 'tr' : DisabledRow;

                return (
                  <Row key={lead.id}>
                    <CheckboxCell>
                      <CheckBtn
                        checked={isSelected}
                        disabled={!canSelect}
                        onClick={() => canSelect && toggleLead(lead.id)}
                      >
                        {isSelected ? <FaCheckSquare /> : <FaSquare />}
                      </CheckBtn>
                    </CheckboxCell>
                    <td style={{ fontWeight: 500, color: '#fff' }}>{lead.name}</td>
                    <td>{lead.phone || 'No phone'}</td>
                    <td>{lead.city || '-'}</td>
                    <td>
                      <PipelineBadge color={pipeInfo.color}>{pipeInfo.label}</PipelineBadge>
                    </td>
                    <td>{statusName}</td>
                    <td>{formatDate(lead.updatedAt)}</td>
                  </Row>
                );
              })}
              {paginatedLeads.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    No leads match your filters
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </TableWrapper>

        <div style={{ marginTop: 16 }}>
          <Pagination
            totalItems={filteredLeads.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            paginate={setCurrentPage}
            itemsPerPageOptions={[25, 50, 100, 200]}
            onItemsPerPageChange={(val) => { setItemsPerPage(val); setCurrentPage(1); }}
          />
        </div>

        {selectedIds.size > 0 && (
          <SelectedCount>
            <FaCheckSquare />
            {selectedIds.size} lead{selectedIds.size !== 1 ? 's' : ''} selected
          </SelectedCount>
        )}
      </Section>

      {/* ── Section 2: Message ── */}
      <Section>
        <SectionTitle>
          <span className="step">2</span>
          Compose Message
        </SectionTitle>

        <TemplateSelector onSelect={handleTemplateSelect} selectedId={selectedTemplateId} />

        <MessageTextarea
          ref={messageRef}
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Type your message or select a template above..."
          disabled={sending}
        />
        <CharCount count={message.length}>{message.length} characters</CharCount>

        <div style={{ marginTop: 12 }}>
          <VariableInserter onInsert={handleInsertVariable} />
        </div>

        {message && (
          <PreviewBox>
            <div className="preview-label">
              <FaEye size={12} />
              Preview {previewLead ? `(${previewLead.name})` : '(sample data)'}
            </div>
            <div className="preview-text">{previewText}</div>
          </PreviewBox>
        )}
      </Section>

      {/* ── Section 3: Rate Limiting ── */}
      <Section>
        <SectionTitle>
          <span className="step">3</span>
          Send Rate
        </SectionTitle>

        <SliderRow>
          <span style={{ color: '#ccc', fontSize: '0.84rem', whiteSpace: 'nowrap' }}>1 message every</span>
          <Slider
            type="range"
            min={1}
            max={10}
            value={delaySeconds}
            onChange={e => setDelaySeconds(Number(e.target.value))}
          />
          <SliderValue>{delaySeconds} second{delaySeconds !== 1 ? 's' : ''}</SliderValue>
        </SliderRow>

        {selectedIds.size > 0 && (
          <EstimateTime>
            <FaClock />
            Estimated time: <strong>{estimatedTime}</strong> for {selectedIds.size} message{selectedIds.size !== 1 ? 's' : ''}
          </EstimateTime>
        )}

        <InfoText>
          <FaInfoCircle />
          <span>
            Twilio recommends sending no more than 1 message per second per phone number.
            A 3-second delay is recommended to avoid rate limiting or carrier filtering.
          </span>
        </InfoText>
      </Section>

      {/* ── Section 4: Send ── */}
      <Section>
        <SectionTitle>
          <span className="step">4</span>
          Send
        </SectionTitle>

        {sending || isComplete ? (
          <BulkSmsProgress
            total={progress.total}
            sent={progress.sent}
            succeeded={progress.succeeded}
            failed={progress.failed}
            failedDetails={progress.failedDetails}
            isPaused={isPaused}
            isComplete={isComplete}
            onPause={handlePause}
            onResume={handleResume}
            onCancel={handleCancel}
            onDone={handleDone}
          />
        ) : (
          <>
            {selectedIds.size > 0 && !message.trim() && (
              <WarningBox>
                <FaExclamationTriangle />
                Write a message or select a template before sending.
              </WarningBox>
            )}

            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <FilterToggle type="button" $open={!scheduleMode} onClick={() => setScheduleMode(false)} style={{ marginBottom: 0 }}>
                <FaPaperPlane size={12} /> Send now
              </FilterToggle>
              <FilterToggle type="button" $open={scheduleMode} onClick={() => setScheduleMode(true)} style={{ marginBottom: 0 }}>
                <FaClock size={12} /> Schedule for later
              </FilterToggle>
            </div>

            {scheduleMode ? (
              <>
                <FilterField style={{ marginBottom: 16, maxWidth: 320 }}>
                  <label>Deliver on</label>
                  <DatePicker
                    selected={scheduledAt}
                    onChange={(date) => setScheduledAt(date)}
                    showTimeSelect
                    timeIntervals={15}
                    minDate={new Date()}
                    dateFormat="MM/dd/yyyy h:mm aa"
                    placeholderText="Select date & time..."
                  />
                </FilterField>
                <SendBtn
                  disabled={selectedIds.size === 0 || !message.trim() || !scheduledAt || scheduling}
                  onClick={handleSchedule}
                >
                  {scheduling ? (
                    <><FaSpinner className="spinning" style={{ animation: 'spin 1s linear infinite' }} /> Scheduling...</>
                  ) : (
                    <><FaClock /> Schedule for {selectedIds.size} Lead{selectedIds.size !== 1 ? 's' : ''}</>
                  )}
                </SendBtn>
              </>
            ) : (
              <SendBtn
                disabled={selectedIds.size === 0 || !message.trim()}
                onClick={() => setConfirmModalOpen(true)}
              >
                <FaPaperPlane />
                Send to {selectedIds.size} Lead{selectedIds.size !== 1 ? 's' : ''}
              </SendBtn>
            )}
          </>
        )}
      </Section>

      {/* ── Section 5: Bulk Pipeline Stage Update ── */}
      {selectedIds.size > 0 && (
        <Section>
          <SectionTitle>
            <span className="step"><FaExchangeAlt size={11} /></span>
            Update Pipeline Stage
          </SectionTitle>
          <p style={{ color: '#999', fontSize: '0.84rem', margin: '0 0 16px 0', lineHeight: 1.5 }}>
            Move the <strong style={{ color: '#fff' }}>{selectedIds.size}</strong> selected lead{selectedIds.size !== 1 ? 's' : ''} to a different pipeline stage.
            Useful after sending follow-up messages.
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <FilterField style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
              <select
                value={bulkStage}
                onChange={e => setBulkStage(e.target.value)}
                disabled={updatingStage}
              >
                <option value="">Select new stage...</option>
                {PIPELINE_STAGES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </FilterField>
            <SendBtn
              style={{ width: 'auto', padding: '10px 24px', fontSize: '0.88rem' }}
              disabled={!bulkStage || updatingStage}
              onClick={handleBulkStageUpdate}
            >
              {updatingStage ? (
                <>
                  <FaSpinner className="spinning" style={{ animation: 'spin 1s linear infinite' }} />
                  Updating {stageProgress.done}/{stageProgress.total}...
                </>
              ) : (
                <>
                  <FaExchangeAlt />
                  Update {selectedIds.size} Lead{selectedIds.size !== 1 ? 's' : ''}
                </>
              )}
            </SendBtn>
          </div>
        </Section>
      )}

      {/* Confirm Modal */}
      <Modal
        title="Confirm Bulk Send"
        isOpen={confirmModalOpen}
        onCancel={() => setConfirmModalOpen(false)}
        onConfirm={startSending}
        confirmText={`Send ${selectedIds.size} Messages`}
      >
        <p style={{ color: '#ccc', lineHeight: 1.6 }}>
          You are about to send <strong style={{ color: '#fff' }}>{selectedIds.size}</strong> SMS message{selectedIds.size !== 1 ? 's' : ''}.
          Each message will be sent individually with a <strong style={{ color: '#fff' }}>{delaySeconds}-second</strong> delay between sends.
        </p>
        <p style={{ color: '#ccc', lineHeight: 1.6 }}>
          Estimated time: <strong style={{ color: '#fff' }}>{estimatedTime}</strong>.
          This action cannot be undone.
        </p>
      </Modal>

      {/* Feedback */}
      <ModalNotification
        show={feedback.show}
        message={feedback.message}
        success={feedback.success}
        onComplete={() => setFeedback(prev => ({ ...prev, show: false }))}
      />
    </DashboardLayout>
  );
};

export default BulkSmsPage;
